/**
 * Query keys de faturas avulsas (`student_invoices`) — lista da página
 * /faturas e totais agregados dos cards.
 *
 * O root preserva o prefixo legado `['student-invoices']` de propósito:
 * as mutações existentes (markPaid em useStudentInvoices, useChargeInvoiceLink,
 * useSelfSignupLeads, Opportunities) invalidam por esse prefixo — mudar o
 * root quebraria a invalidation silenciosamente (prefix-match).
 */
import { tuple } from "./core";

const root = ["student-invoices"] as const;

export interface StudentInvoiceListFilters {
  clubId: string | null;
  status: string | null;
  /** Competência 'yyyy-MM' sobre due_date. */
  competencia: string | null;
  search: string;
  /** Situação do aviso ao aluno (`situacaoDoAviso`, lib/invoice-notice-status do CRM). */
  aviso?: string | null;
  /** Envio pela recepção (`envioDaFatura`). */
  envio?: string | null;
}

export const invoices = {
  /** Prefixo raiz — igual ao legado; mutações invalidam isto. */
  all: root,
  /**
   * Faturas vivas das reservas visíveis na Agenda (selo e "Cobrar esta
   * reserva", cobrança avulsa PR5). Sob a RAIZ das faturas de propósito: toda
   * mutação de fatura (cobrar, baixar, cancelar em /faturas) já invalida
   * `invoices.all`, e o selo acompanha sem lista de invalidação própria.
   * Chaveada pelos IDS (ordenados) — derivada do resultado de `agenda.bookings`.
   */
  cobrancasDasReservas: (bookingIds: readonly string[] | null | undefined) => tuple(root, "cobrancas-das-reservas", bookingIds),
  /** Lista paginada/filtrada da página /faturas. */
  list: (
    clubId: string | null | undefined,
    viewScope: unknown,
    scopedTeacherId: string | null | undefined,
    filters: StudentInvoiceListFilters | null,
    page: number,
    pageSize: number,
  ) => tuple(root, "list", clubId, viewScope, scopedTeacherId, filters, page, pageSize),
  /** Totais dos cards (query agregada, sem paginação/status/search). */
  totals: (
    clubId: string | null | undefined,
    viewScope: unknown,
    scopedTeacherId: string | null | undefined,
    clubFilter: string | null,
  ) => tuple(root, "totals", clubId, viewScope, scopedTeacherId, clubFilter),
  /**
   * Prévia da troca de meio (RPC alterar_meio_de_cobranca com p_simular).
   * Raiz PRÓPRIA, fora de student-invoices de propósito: a execução invalida
   * qk.invoices.all, e uma prévia sob essa raiz se refaria em silêncio logo
   * depois de executar, mostrando outro resultado que o do que foi feito.
   */
  previaDaTroca: (pedido: unknown) => tuple(["troca-de-meio"] as const, "previa", pedido),
  /**
   * UMA fatura (detalhe lateral). Sob `student-invoices`: baixa, cobrança e
   * "marcar como enviado" já invalidam `qk.invoices.all` e o detalhe acompanha.
   */
  detail: (invoiceId: string | null | undefined) => tuple(root, "detail", invoiceId),
  /** Linha do tempo da fatura (RPC `get_invoice_timeline`). */
  timeline: (invoiceId: string | null | undefined) => tuple(root, "timeline", invoiceId),
  /**
   * Comentários da parcela (RPC `list_invoice_comments`, 20261009130300). Sob
   * `student-invoices`: comentar invalida `qk.invoices.all`, e a lista, o
   * contador e o detalhe se refazem juntos.
   */
  comments: (invoiceId: string | null | undefined) => tuple(root, "comments", invoiceId),
  /** Contador de comentários das faturas de uma página (RPC `invoice_comment_counts`). */
  commentCounts: (invoiceIds: readonly string[]) => tuple(root, "comment-counts", invoiceIds),
  /**
   * Histórico de alterações do RECORTE de /faturas (RPC eventos_do_recorte,
   * 20261005160300). `args` = argumentosDoHistorico (o recorte + as canceladas)
   * sem `p_hoje` (+ professor).
   * Sob o root `student-invoices`: toda mutação de fatura (baixa, valor, troca
   * de meio) já invalida.
   */
  historicoDoRecorte: (args: unknown, page: number, pageSize: number) =>
    tuple(root, "historico", "recorte", args, page, pageSize),
  /**
   * Contexto do gerador mensal para o "Alterar valor" (`aguardando_mensalidade`,
   * lib/valor-da-fatura do CRM): os grupos (dia, próximo ciclo) do gerador por
   * aluno das faturas candidatas (RPC ciclos_do_gerador, 20261006120000). Sob
   * `student-invoices`: mudar valor ou gerar a mensalidade invalida junto com a
   * lista. `clubId` = o clube consultado (só o ativo, e só com invoices.manage).
   */
  ciclosDoGerador: (clubId: string | null, alunos: readonly string[]) =>
    tuple(root, "ciclos-do-gerador", clubId, alunos),
  /**
   * Configuração de notificações do customer no Asaas (edge
   * `asaas-customer-notifications`). Raiz PRÓPRIA, fora de `student-invoices`:
   * é uma chamada externa, só leitura, e não deve refazer a cada baixa ou
   * envio registrado na lista.
   */
  asaasNotifications: (invoiceId: string | null | undefined) =>
    tuple(["asaas-customer-notifications"] as const, invoiceId),
  /**
   * Simulação do "Cobrar em atraso" (edge `cobrar-em-atraso` com `simular`):
   * travas do servidor e meios do clube para o diálogo. Raiz PRÓPRIA, fora de
   * `student-invoices`, pelo mesmo motivo da prévia da troca: a emissão
   * invalida qk.invoices.all, e a simulação refeita em silêncio diria
   * "cobrança viva" sobre a cobrança que o próprio diálogo acabou de criar.
   */
  cobrancaEmAtraso: (invoiceId: string | null | undefined) =>
    tuple(["cobranca-em-atraso"] as const, "simulacao", invoiceId),
  /**
   * Dados do CLUBE que o recibo de pagamento imprime (razão social, CNPJ,
   * cidade, fuso). Raiz própria: não muda com baixa nem cobrança.
   */
  clubeDoRecibo: (clubId: string | null | undefined) =>
    tuple(["recibo-de-pagamento"] as const, "clube", clubId),
  /** Clubes do escopo com NFS-e configurada (provedor + empresa + plano). */
  nfseConfigurada: (clubIds: readonly string[]) =>
    tuple(["nfse-configurada"] as const, clubIds),
  /**
   * Cobranças da FICHA do aluno (aba Cobranças da Jornada, 19/10/2026), em três
   * leituras: o resumo leve por ano, as em aberto (a seção "Precisa de
   * atenção") e as de UM ano, buscadas sob demanda. Sob `student-invoices`
   * de propósito: cobrar, dar baixa, mudar valor e a baixa/reemissão da
   * mensalidade já invalidam `invoices.all`, e a ficha acompanha sem lista de
   * invalidação própria.
   */
  fichaDoAluno: {
    anos: (studentId: string | null | undefined) => tuple(root, "ficha", studentId, "anos"),
    emAberto: (studentId: string | null | undefined) => tuple(root, "ficha", studentId, "em-aberto"),
    ano: (studentId: string | null | undefined, ano: string) => tuple(root, "ficha", studentId, "ano", ano),
  },
} as const;
